import React from "react";
import { API } from 'aws-amplify'
import { listMarkets } from '../graphql/queries'
import MyCard from "../components/ProductItem"
import { Link } from 'react-router-dom'
import styled from 'styled-components';
import Hero from '../components/Home/Hero'
import ProductSearch from './search/ProductSearch'
import { GNav, GNavbarContainer, GbarLink, TypeScroller } from './TypeNav'
import { HomePageSkeleton } from './SkeletonCard'

/* ---------- market list section ---------- */
export const SectionTitle = styled.h2`
  max-width: 1180px;
  margin: 28px auto 4px;
  padding: 0 16px;
  color: #1f2d3d;
  font-size: clamp(1.5rem, 3vw, 2rem);
  font-weight: 800;
  letter-spacing: -0.01em;
  display: flex;
  align-items: center;
  gap: 10px;

  @media screen and (min-width: 768px) {
    padding: 0 24px;
  }
`;

export const SectionSub = styled.p`
  max-width: 1180px;
  margin: 0 auto 4px;
  padding: 0 16px;
  color: #6a7b8a;
  font-size: 1rem;

  @media screen and (min-width: 768px) {
    padding: 0 24px;
  }
`;

export const MarketsWrap = styled.div`
  max-width: 1180px;
  margin: 0 auto;
  padding: 8px 16px 40px;

  @media screen and (min-width: 768px) {
    padding: 8px 24px 48px;
  }
`;

export const MarketBlock = styled.div`
  margin: 30px 0;
  scroll-margin-top: 170px; /* clear the two sticky bars when jumped to */
`;

export const MarketHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  background: #fff;
  border: 1px solid rgba(31, 45, 61, 0.08);
  border-left: 4px solid #01bf71;
  border-radius: 14px;
  padding: 14px 18px;
  box-shadow: 0 6px 18px rgba(31, 45, 61, 0.05);
  margin-bottom: 16px;
`;

export const MarketNameWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

export const MarketName = styled(Link)`
  font-size: clamp(1.1rem, 2.2vw, 1.4rem);
  font-weight: 700;
  color: #1f2d3d;
  text-decoration: none;

  &:hover {
    color: #01bf71;
  }
`;

export const CountBadge = styled.span`
  flex: 0 0 auto;
  background: rgba(1, 191, 113, 0.12);
  color: #0a8f57;
  font-weight: 700;
  font-size: 0.8rem;
  padding: 4px 10px;
  border-radius: 999px;
  white-space: nowrap;
`;

export const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

export const TagPill = styled.span`
  background: rgba(255, 154, 60, 0.14);
  color: #c96a12;
  font-size: 0.76rem;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 999px;
  white-space: nowrap;
`;

/* scoped, bulletproof responsive grid (avoids the fragile global .productgrid
   rule whose nested min()/minmax() can collapse to one column in some browsers) */
export const ProductGrid = styled.div`
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(2, 1fr); /* 2 columns on mobile */

  @media (min-width: 580px) {
    gap: 12px;
  }
  @media (min-width: 640px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media (min-width: 960px) {
    grid-template-columns: repeat(4, 1fr);
  }
  @media (min-width: 1280px) {
    grid-template-columns: repeat(5, 1fr);
  }
`;

/* ---------- fresh arrivals (horizontal snap row) ---------- */
export const FeaturedWrap = styled.div`
  max-width: 1180px;
  margin: 26px auto 0;
  padding: 0 16px;

  @media screen and (min-width: 768px) {
    padding: 0 24px;
  }
`;

export const FeaturedTitle = styled.h3`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 12px;
  font-size: 1.2rem;
  font-weight: 800;
  color: #1f2d3d;
`;

export const FeaturedRow = styled.div`
  display: flex;
  gap: 12px;
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: 10px;
  -webkit-overflow-scrolling: touch;
  scroll-snap-type: x proximity;
  overscroll-behavior-x: contain;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const FeaturedCell = styled.div`
  flex: 0 0 165px;
  scroll-snap-align: start;

  @media screen and (min-width: 480px) {
    flex-basis: 190px;
  }
`;

/* ---------- testimonials ---------- */
export const QuotesWrap = styled.section`
  max-width: 1180px;
  margin: 12px auto 0;
  padding: 0 16px;

  @media screen and (min-width: 768px) {
    padding: 0 24px;
  }
`;

export const QuotesTitle = styled.h3`
  margin: 0 0 16px;
  text-align: center;
  font-size: clamp(1.3rem, 2.6vw, 1.7rem);
  font-weight: 800;
  color: #1f2d3d;
`;

export const QuotesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;

  @media screen and (min-width: 820px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

export const QuoteCard = styled.figure`
  margin: 0;
  background: #fff;
  border: 1px solid rgba(31, 45, 61, 0.07);
  border-radius: 16px;
  padding: 22px;
  box-shadow: 0 6px 18px rgba(31, 45, 61, 0.04);
`;

export const Stars = styled.div`
  color: #ff9a3c;
  letter-spacing: 2px;
  margin-bottom: 10px;
`;

export const QuoteText = styled.blockquote`
  margin: 0 0 12px;
  color: #46586a;
  font-size: 0.96rem;
  line-height: 1.55;
`;

export const QuoteName = styled.figcaption`
  color: #8a99a8;
  font-size: 0.85rem;
  font-weight: 700;
`;

/* ---------- visit-us strip ---------- */
export const VisitStrip = styled.section`
  max-width: 1180px;
  margin: 24px 16px 0;
  padding: 22px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  background: linear-gradient(135deg, #12313f 0%, #1f2d3d 100%);
  border-radius: 20px;
  color: #fff;

  @media screen and (min-width: 768px) {
    margin: 28px auto 0;
    padding: 26px 28px;
  }
`;

export const VisitHead = styled.h3`
  margin: 0 0 6px;
  font-size: 1.25rem;
  font-weight: 800;
`;

export const VisitText = styled.p`
  margin: 0;
  color: rgba(255, 255, 255, 0.72);
  font-size: 0.95rem;
`;

export const VisitBtn = styled(Link)`
  flex: 0 0 auto;
  background: #01bf71;
  color: #fff;
  font-weight: 700;
  text-decoration: none;
  padding: 12px 26px;
  border-radius: 999px;
  box-shadow: 0 8px 20px rgba(1, 191, 113, 0.35);
  transition: background 0.15s ease, transform 0.15s ease;

  &:hover {
    background: #04a763;
    transform: translateY(-1px);
  }
`;

const quotes = [
  {
    text: 'Fresh vegetables and every spice I need in one stop — it feels like shopping back home.',
    name: 'Priya · Concord',
  },
  {
    text: 'Ordered in the evening, groceries at my door the next day. Daily delivery is a game changer.',
    name: 'Arjun · Walnut Creek',
  },
  {
    text: 'The oldest Indian grocery in the area for a reason — quality you can trust.',
    name: 'Meera · Pleasant Hill',
  },
];

export const ViewAll = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100%;
  border: 2px dashed rgba(1, 191, 113, 0.4);
  border-radius: 12px;
  color: #0a8f57;
  font-weight: 700;
  text-decoration: none;
  padding: 24px;
  text-align: center;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: rgba(1, 191, 113, 0.06);
    border-color: #01bf71;
  }
`;

class GMarketAltList extends React.Component {
    Mounted = false;

    state = {
        markets: null,
        isLoading: true,
    };


    componentDidMount() {
        this.Mounted = true;
        this.handleGetMarketList()
    }

    handleGetMarketList = async () => {
        try {
            const result = await API.graphql({
                query: listMarkets,
                authMode: "API_KEY"
            });

            if (this.Mounted) {
                this.setState({ markets: result.data.listMarkets.items, isLoading: false });
            }
        } catch (err) {
            console.error('Error fetching listmarkets', err);
        }
    }

    componentWillUnmount() {
        this.Mounted = false;
    }

    render() {
        const { markets, isLoading } = this.state;

        const freshArrivals = (markets || [])
            .flatMap(m => (m.products && m.products.items ? m.products.items : []))
            .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
            .slice(0, 10);

        // --- Suggestion: Add a Best Sellers section ---
        const bestSellers = (markets || [])
            .flatMap(m => (m.products && m.products.items ? m.products.items : []))
            .sort((a, b) => (b.orders || 0) - (a.orders || 0)) // Assuming you add an 'orders' count to products
            .slice(0, 5);

        return isLoading ? (
            <HomePageSkeleton sections={2} cardsPerSection={8} />
        ) : (
            <>
                <ProductSearch markets={markets}>
                <Hero />

                <SectionTitle id="markets-list">
                    <span role="img" aria-label="shopping cart">🛒</span> Explore our markets
                </SectionTitle>
                <SectionSub>
                    Fresh picks from every aisle — tap a category to jump, or browse below.
                </SectionSub>

                <GNav aria-label="Product categories">
                    <GNavbarContainer>
                        <TypeScroller>
                            {markets.map(market => (
                                <GbarLink to={`${market.name}`}
                                    key={market.id}
                                    smooth={true}
                                    duration={500}
                                    spy={true}
                                    exact='true'
                                    offset={-80}
                                    className="bg-primary text-white display-inline-block rounded-corners-big item-price pleftright" >
                                    {market.name}
                                </GbarLink>
                            ))}
                        </TypeScroller>
                    </GNavbarContainer>
                </GNav>

                {/* --- Suggested "Best Sellers" Section --- */}
                {bestSellers.length > 0 && (
                    <FeaturedWrap>
                        <FeaturedTitle>
                            <span role="img" aria-label="fire">🔥</span> Best Sellers
                        </FeaturedTitle>
                        <FeaturedRow>
                            {bestSellers.map(product => (
                                <FeaturedCell key={product.id}>
                                    <MyCard product={product} />
                                </FeaturedCell>
                            ))}
                        </FeaturedRow>
                    </FeaturedWrap>
                )}

                {freshArrivals.length > 0 && (
                    <FeaturedWrap>
                        <FeaturedTitle>
                            <span role="img" aria-label="sparkles">✨</span> Fresh arrivals
                        </FeaturedTitle>
                        <FeaturedRow>
                            {freshArrivals.map(product => (
                                <FeaturedCell key={product.id}>
                                    <MyCard product={product} />
                                </FeaturedCell>
                            ))}
                        </FeaturedRow>
                    </FeaturedWrap>
                )}

                <QuotesWrap>
                    <QuotesTitle>Loved by the community</QuotesTitle>
                    <QuotesGrid>
                        {quotes.map(q => (
                            <QuoteCard key={q.name}>
                                <Stars aria-label="5 out of 5 stars">★★★★★</Stars>
                                <QuoteText>&ldquo;{q.text}&rdquo;</QuoteText>
                                <QuoteName>{q.name}</QuoteName>
                            </QuoteCard>
                        ))}
                    </QuotesGrid>
                </QuotesWrap>

                <MarketsWrap>
                    {markets.map(market => (
                        <MarketBlock key={market.id} id={market.name}>
                            <MarketHeader>
                                <MarketNameWrap>
                                    <MarketName to={`/markets/${market.id}`}>
                                        {market.name}
                                    </MarketName>
                                    {market.products.items && (
                                        <CountBadge>
                                            {market.products.items.length} items
                                        </CountBadge>
                                    )}
                                </MarketNameWrap>

                                {market.tags && market.tags.length > 0 && (
                                    <TagRow>
                                        {market.tags.map(tag => (
                                            <TagPill key={tag}>{tag}</TagPill>
                                        ))}
                                    </TagRow>
                                )}
                            </MarketHeader>

                            <ProductGrid>
                                {market.products.items && market.products.items.slice(0, 10).map(product => (
                                    <MyCard key={product.id} product={product} />
                                ))}

                                <ViewAll to={`/markets/${market.id}`}>
                                    View all products →
                                </ViewAll>
                            </ProductGrid>
                        </MarketBlock>
                    ))}
                </MarketsWrap>

                <VisitStrip>
                    <div>
                        <VisitHead>Visit our Concord store</VisitHead>
                        <VisitText>
                            1859 Adobe St, Concord, CA · Open daily 12–8 PM · (925) 825-9043
                        </VisitText>
                    </div>
                    <VisitBtn to="/about">Our story →</VisitBtn>
                </VisitStrip>
                </ProductSearch>
            </>
        )
    }
}

export default GMarketAltList;
